import {
  IconBedOutlined,
  IconDoorOpenOutlined,
  IconExternalOutlined,
  IconHousePlusOutlined,
  IconSofaOutlined,
  IconToolOutlined,
} from '@/components/icons';
import { isProduction } from '@/lib/env';
import {
  AccountBookOutlined,
  CommentOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  ScanOutlined,
  SettingOutlined,
  SoundOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { MenuProps } from 'antd';

type MenuItem = Required<MenuProps>['items'][number];

/**
 * Two-Level menu style
 *
 * - child menu의 key 값이 /{부모메뉴의 key값}/bulabula 와 같은 형태여야 함.
 */
export const sidebarMenuItems = [
  {
    key: 'mangrover',
    icon: <UserOutlined />,
    label: '이용자',
    children: [
      {
        key: '/mangrover/manage',
        label: '맹그로버 관리',
      },
      {
        key: '/mangrover/credits',
        label: '크레딧 관리',
      },
    ],
  },
  {
    key: 'community',
    icon: <CommentOutlined />,
    label: '커뮤니티',
    children: [
      {
        key: '/community/post',
        label: '게시글 관리',
      },
      {
        key: '/community/category',
        label: '카테고리 관리',
      },
      {
        key: '/community/msc',
        label: 'MSC 관리',
      },
    ],
  },
  {
    key: 'sale',
    icon: <IconBedOutlined width={12} height={12} />,
    label: 'LIVE 판매',
    children: [
      {
        key: '/sale/slot',
        label: '판매객실관리',
      },
      {
        key: '/sale/contract',
        label: '계약신청관리',
      },
      {
        key: '/sale/vacancy',
        label: '공실대기관리',
      },
      {
        key: '/sale/price',
        label: '객실가격관리',
      },
      {
        key: '/sale/branch',
        label: '지점정보관리',
      },
      {
        key: '/sale/renewal',
        label: '갱신여부관리',
      },
      {
        type: 'divider',
      },
      {
        key: '/sale/tour-schedule',
        label: '투어 스케쥴',
      },
      {
        key: '/sale/tour-reservation',
        label: '투어 예약 현황',
      },
    ],
  },
  {
    key: 'stay',
    icon: <IconSofaOutlined width={12} height={12} />,
    label: 'STAY 판매',
    children: [
      {
        key: '/stay/manage',
        label: '스테이 출입관리',
      },
      {
        key: '/stay/reservation',
        label: '스테이 예약 관리',
      },
      {
        key: '/stay/consent-history',
        label: '스테이 약관 이력',
      },
    ],
  },
  {
    key: 'room',
    icon: <IconDoorOpenOutlined width={12} height={12} />,
    label: '객실',
    children: [
      {
        key: '/room/status',
        label: '객실 현황',
      },
      {
        key: '/room/door-lock-manage',
        label: '도어락 관리',
      },
      {
        key: '/room/cardkey-stay',
        label: 'STAY 카드키 관리',
      },
    ],
  },
  {
    key: 'facility',
    icon: <IconToolOutlined width={12} height={12} />,
    label: '시설',
    children: [
      {
        key: '/facility/inquiry',
        label: '시설 예약 관리',
      },
      {
        key: '/facility/add',
        label: '시설 예약 추가',
        icon: <IconExternalOutlined width={12} height={12} />,
      },

      {
        key: '/facility/space',
        label: '공용공간 관리',
      },
      {
        key: '/facility/spaces-reservations',
        label: '공용공간 예약 관리',
      },
    ],
  },

  /**
   * 출입 그룹 관리 메뉴는 추후 개선의 여지가 있음. (현재 하위 메뉴 하드코딩된 구조)
   * 관심있다면 지라 카드 참조: https://mgrv.atlassian.net/browse/MT-4292
   */
  {
    key: 'user-group',
    icon: <ScanOutlined width={12} height={12} />,
    label: '출입 그룹',
    disabled: isProduction,
    children: [
      {
        key: '/user-group/manage',
        label: '출입 그룹 관리',
      },
    ],
  },
  {
    key: 'visitor',
    icon: <IdcardOutlined width={12} height={12} />,
    label: '방문자',
    children: [
      {
        key: '/visitor/manage',
        label: '방문자 관리',
      },
    ],
  },
  {
    key: 'addition',
    icon: <IconHousePlusOutlined width={12} height={12} />,
    label: '부가서비스',
    children: [
      {
        key: '/addition/manage',
        label: '부가서비스 관리',
      },
    ],
  },
  {
    key: 'notice',
    icon: <SoundOutlined />,
    label: '공지사항',
    children: [
      {
        key: '/notice/manage',
        // 긴급공지 통합(VC-1489)으로 앱 공지 관리 화면임을 명시한다.
        label: '앱 공지사항 관리',
      },
    ],
  },
  {
    key: 'branch-region',
    icon: <EnvironmentOutlined />,
    label: '지점',
    children: [
      {
        key: '/branch-region/manage',
        label: '권역 관리',
      },
      {
        key: '/branch-region/mapping',
        label: '지점-권역 매핑',
      },
      {
        key: '/branch-region/lifestyle-tag',
        label: '라이프스타일 태그',
      },
      {
        key: '/branch-region/card-attribute',
        label: '지점 카드 관리',
      },
      {
        key: '/branch-region/sales-info',
        label: '지점 판매 정보',
      },
      {
        key: '/branch-region/branch-init',
        label: '지점 생성 관리',
      },
      {
        key: '/branch-region/notification-branch-settings',
        label: '지점 알림 관리',
      },
      {
        key: '/branch-region/branches',
        label: '구)지점 관리',
      },
    ],
  },
  {
    key: 'billing',
    icon: <AccountBookOutlined />,
    label: '관리비',
    children: [
      {
        key: '/billing/regular',
        label: '정기청구',
      },
      {
        key: '/billing/move-out',
        label: '퇴실청구',
      },
      {
        key: '/billing/general-management-fee',
        label: '일반관리비 설정',
      },
    ],
  },
  {
    key: 'daily-pricing',
    icon: <DollarOutlined />,
    label: '데일리 프라이싱',
    children: [
      {
        key: '/daily-pricing/metrics',
        label: '지표 조회',
      },
      {
        key: '/daily-pricing/summary',
        label: '전체 요약',
      },
      {
        key: '/daily-pricing/settings',
        label: '지점 설정',
      },
    ],
  },
  {
    key: 'setting',
    icon: <SettingOutlined />,
    label: '관리',
    children: [
      {
        key: '/setting/manual-authorizer',
        label: '관리자',
      },
      {
        key: '/setting/terms',
        label: '약관 관리',
      },
      {
        key: '/setting/short-url',
        label: '단축 URL 관리',
      },
      {
        key: 'https://docs.google.com/spreadsheets/d/1i_kUQWQgYToK_05Ir_8h3Hr_9tpzjW4S0-RTxhG7Mng/edit?gid=1486828590#gid=1486828590',
        icon: <IconExternalOutlined width={12} height={12} />,
        label: 'DX개발용어사전',
      },
    ],
  },
] as const satisfies MenuItem[];

/**
 * 자체 사이드바 메뉴는 없지만 특정 메뉴에 소속되어 하이라이트를 공유하는 경로 매핑.
 * (예: 공용공간 예약 달력은 별도 메뉴 없이 "공용공간 예약 관리" 메뉴를 활성화한다.)
 *
 * key: 실제 라우트 pathname, value: 활성화할 사이드바 메뉴 key
 */
export const menuKeyAliases: Record<string, string> = {
  '/facility/space-calendar': '/facility/spaces-reservations',
  '/facility/schedule': '/facility/inquiry',
  // 제주 전용이던 구 경로(리다이렉트 전 잠깐 머무는 동안 탭이 어긋나지 않도록)
  '/stay/manage-jeju': '/stay/reservation',
  '/stay/consent-history-jeju': '/stay/consent-history',
};
